import React from 'react';
import { Avatar, AvatarStack } from './Avatar';
import { ensureIdentityStyles } from './styles';
import { Person, Team } from './helpers';

type Variant = 'inline' | 'row' | 'podium';

export function TeamChip({ team }: { team: { name?: string | null; profileUrl?: string | null } }) {
  const label = team.name || 'team';
  // The label is wrapped rather than being a bare child for the same reason Name() is:
  // .ac-teamchip is a flex container, and text-overflow never applies to one. The
  // ellipsis has to live on an element inside it.
  const inner = <span className="ac-teamchip-label">{label}</span>;
  return team.profileUrl
    ? <a className="ac-teamchip" href={team.profileUrl} title={label}>{inner}</a>
    : <span className="ac-teamchip" title={label}>{inner}</span>;
}

// The no-href branch must render an element, not a Fragment: a bare text node cannot
// be a flex item, so it can never take `text-overflow: ellipsis` and would hard-cut
// mid-glyph. `title` keeps the full value recoverable when the label is truncated.
function Name({ name, href }: { name?: string | null; href?: string | null }) {
  const label = name || 'Anonymous';
  return href
    ? <a className="ac-namelink" href={href} title={label}>{label}</a>
    : <span className="ac-name" title={label}>{label}</span>;
}

function teamSub(team: Team): string {
  const n = team.memberCount != null ? team.memberCount : (team.members ? team.members.length : null);
  if (n == null) return 'team';
  const base = n + (n === 1 ? ' member' : ' members');
  return team.affiliation ? base + ' · ' + team.affiliation : base;
}

export function Competitor(props: {
  participant?: Person | null;
  team?: Team | null;
  variant?: Variant;
  official?: boolean;
  meta?: React.ReactNode;
}) {
  React.useEffect(() => { ensureIdentityStyles(); }, []);
  const variant: Variant = props.variant || 'row';
  const isTeam = !!props.team && !props.participant;
  const team = props.team || null;
  const p = props.participant || null;

  const name = isTeam ? (team && team.name) : (p && p.name);
  const href = isTeam ? (team && team.profileUrl) : (p && p.profileUrl);
  const sub = isTeam && team ? teamSub(team) : (p && p.affiliation) || '—';

  const avatar = isTeam && team && team.members && team.members.length
    ? <AvatarStack members={team.members} max={variant === 'podium' ? 2 : 3} total={team.memberCount != null ? team.memberCount : undefined} />
    : <Avatar name={name} src={(p && p.avatarUrl) || undefined} avatarVersion={p && p.avatarVersion} />;

  const nameLine = (
    <span className="ac-id-name">
      <Name name={name} href={href} />
      {props.official && <span className="ac-official">official</span>}
      {!isTeam && props.team && <TeamChip team={props.team} />}
      {props.meta}
    </span>
  );

  if (variant === 'inline') {
    return <span className="ac-id ac-id--inline"><Avatar name={name} src={(p && p.avatarUrl) || undefined} avatarVersion={p && p.avatarVersion} />{nameLine}</span>;
  }

  return (
    <div className={'ac-id ac-id--' + variant}>
      {avatar}
      <div className="ac-id-text">
        {nameLine}
        <span className="ac-id-sub" title={sub}>{sub}</span>
        {variant === 'podium' && isTeam && team && team.members && team.members.length > 0 && (
          <span className="ac-roster">
            {team.members.map((m, i) => (
              <span key={(m.name || '') + i} className="m"><span className={'dot' + (m.isCaptain ? ' cap' : '')}></span><Name name={m.name} href={m.profileUrl} /></span>
            ))}
          </span>
        )}
      </div>
    </div>
  );
}
